import React from 'react';
import Icon from '../shared/Icon';
import { formatTimestamp } from '../../../shared/datetime/timestamps';
import { showFullTextIfTruncated } from '../../../utils/truncationTooltip';
import { assessmentStatusLabel, formatDuration, isTranscriptSource } from '../../../models/AssessmentsModel';
import { ArchivedTip } from '../Library/LibraryDocumentMenu';
import AssessmentStatusChip from './AssessmentStatusChip';
import AssessmentMenu from './AssessmentMenu';

/**
 * An assessment as a Dataset Card, on the Library card's layout (PRD-1703).
 * The icon says what it was built on: a waveform for a recording, the
 * struck-through waveform for a transcript. Under the name, its scenario, then
 * its run time and file; a transcript has no run time, so that line is the
 * file alone. The card opens the assessment's detail (`onOpen`), from a click
 * or Enter or Space; its menu's clicks and keys stay with the menu.
 */
function AssessmentCard({ assessment, scenarioName, actions, onOpen }) {
  const transcript = isTranscriptSource(assessment);
  // Only the card's own click or key, not one from its menu (portalled, but
  // still bubbling here through React).
  const fromCard = (e) => e.currentTarget.contains(e.target)
    && (e.target === e.currentTarget || !e.target.closest('button, a, [role="menuitem"]'));
  // Keys open it from the card, or from its name once an action has sent focus
  // there (tabIndex -1), not from a control inside it.
  const keyFromCard = (e) => e.target === e.currentTarget
    || (e.currentTarget.contains(e.target) && e.target.tabIndex < 0);
  return (
    <div
      className={`resui-card resui-card--elevated resui-card--interactive library-card assessment-card${assessment.archived ? ' is-archived' : ''}`}
      data-component="Dataset Card"
      role="button"
      tabIndex={0}
      aria-label={`${assessment.name}, ${scenarioName}, ${assessmentStatusLabel(assessment)}`}
      onClick={(e) => { if (fromCard(e)) onOpen(assessment, e.currentTarget); }}
      onKeyDown={(e) => {
        if (!keyFromCard(e) || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        onOpen(assessment, e.currentTarget);
      }}
    >
      <div className="library-card-icon">
        <Icon name={transcript ? 'waveform-slash' : 'waveform'} size={18} />
      </div>
      <div data-asm-focus={assessment.id} tabIndex={-1} className="library-card-name" onMouseEnter={showFullTextIfTruncated}>{assessment.name}</div>
      <div className="library-card-subs">
        <div className="library-card-sub" onMouseEnter={showFullTextIfTruncated}>{scenarioName}</div>
        <div className="library-card-sub assessment-card-meta">
          {!transcript && (
            <>
              <span>{formatDuration(assessment.durationSec)}</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          <span className="assessment-card-file" onMouseEnter={showFullTextIfTruncated}>{assessment.fileName}</span>
        </div>
      </div>
      <div className="library-card-foot">
        <AssessmentStatusChip assessment={assessment} />
        <span className="library-card-date" title={formatTimestamp(assessment.uploadedAt, 'long')}>{formatTimestamp(assessment.uploadedAt, 'short')}</span>
      </div>
      <AssessmentMenu assessment={assessment} actions={actions} />
      <ArchivedTip doc={assessment} />
    </div>
  );
}

export default AssessmentCard;
